import { useMutation, useQueryClient } from '@tanstack/react-query';

import { authFetch } from './authFetch';
import { projectPageKeys } from './queryKeys';

interface RenameAnalyticalDatasetInput {
  projectId: string;
  analyticalDatasetProfileId: string;
  name: string;
}

/**
 * Rename an analytical dataset from the artifacts page. PATCHes the profile name;
 * the backend validates uniqueness within the project (409 on a duplicate, surfaced
 * to the caller). Duplicates the PATCH in feature-analytical-datasets on purpose —
 * that lib can't be imported here (dependency cycle).
 */
export const useRenameAnalyticalDatasetMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, RenameAnalyticalDatasetInput>({
    mutationFn: ({ projectId, analyticalDatasetProfileId, name }) =>
      authFetch<void>(
        `/analytical-datasets/${encodeURIComponent(projectId)}` +
          `/analytical-dataset-profile/${encodeURIComponent(analyticalDatasetProfileId)}/rename`,
        {
          method: 'PATCH',
          body: JSON.stringify({ name }),
        }
      ),
    onSuccess: (_data, { projectId }) => {
      // The manifest joins to the canonical profile for the name, so refreshing it
      // surfaces the new name across the artifacts list + activity feed.
      queryClient.invalidateQueries({
        queryKey: projectPageKeys.artifactsForProject(projectId),
      });
      queryClient.invalidateQueries({
        queryKey: projectPageKeys.activityForProject(projectId),
      });
    },
  });
};
